import { z } from 'zod';

import type {
  PatchAdminV1StoresStoreIdWebhooksIdBody,
  PostAdminV1StoresStoreIdWebhooksBody,
  WebhookEndpoint,
} from '@nucleus-commerce/api-client/admin';

import { isEndpointUrl, normaliseSubscriptions, subscriptionIssue } from './webhook-rules';

/** Validated against the event names the backend lists, so a typo never saves silently. */
export function endpointFormSchema(knownEvents: readonly string[]) {
  return z.object({
    url: z
      .string()
      .trim()
      .min(1, { error: 'webhooks:validation.urlRequired' })
      .max(2000, { error: 'webhooks:validation.urlLength' })
      .refine(isEndpointUrl, { error: 'webhooks:validation.url' }),
    description: z.string().trim().max(200, { error: 'webhooks:validation.descriptionLength' }),
    events: z
      .array(z.string())
      .min(1, { error: 'webhooks:validation.eventsRequired' })
      .max(100, { error: 'webhooks:validation.eventsMax' })
      .refine((events) => events.every((e) => subscriptionIssue(e, knownEvents) === null), {
        error: 'webhooks:validation.eventsInvalid',
      }),
  });
}

export type EndpointForm = z.infer<ReturnType<typeof endpointFormSchema>>;

export function emptyEndpointForm(): EndpointForm {
  return { url: '', description: '', events: [] };
}

export function fromEndpoint(endpoint: WebhookEndpoint): EndpointForm {
  return {
    url: endpoint.url,
    description: endpoint.description ?? '',
    events: endpoint.events,
  };
}

export function toEndpointCreate(values: EndpointForm): PostAdminV1StoresStoreIdWebhooksBody {
  return {
    url: values.url,
    events: normaliseSubscriptions(values.events),
    ...(values.description ? { description: values.description } : {}),
  };
}

export function toEndpointUpdate(values: EndpointForm): PatchAdminV1StoresStoreIdWebhooksIdBody {
  return {
    url: values.url,
    description: values.description || null,
    events: normaliseSubscriptions(values.events),
  };
}
